import React, { useState } from 'react';
import {
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  Alert,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/contexts/AuthContext';
import { togglePropuestaFavorita } from '../../lib/services/propuestas-favoritas';

type Props = {
  propuestaId: string;
  favorita: boolean;
  onChange?: (next: boolean) => void;
  style?: StyleProp<ViewStyle>;
  size?: number;
  /** onDark = sobre foto; onLight = sobre fondo claro */
  tone?: 'onDark' | 'onLight';
};

/**
 * Corazón para marcar/desmarcar propuesta favorita (requiere sesión comensal).
 */
export function PropuestaFavoritoButton({
  propuestaId,
  favorita,
  onChange,
  style,
  size = 22,
  tone = 'onDark',
}: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const onLight = tone === 'onLight';

  const showError = (msg: string) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.alert(msg);
    } else {
      Alert.alert('Favoritos', msg);
    }
  };

  const onPress = async () => {
    if (busy) return;
    if (!user?.id) {
      navigate('/entrar');
      return;
    }
    setBusy(true);
    const prev = favorita;
    onChange?.(!prev);
    try {
      const next = await togglePropuestaFavorita(user.id, propuestaId);
      onChange?.(next);
    } catch (err) {
      onChange?.(prev);
      console.error('Error toggle favorito:', err);
      showError(err instanceof Error ? err.message : 'No se pudo actualizar el favorito');
    } finally {
      setBusy(false);
    }
  };

  return (
    <TouchableOpacity
      style={[styles.btn, onLight && styles.btnOnLight, style]}
      onPress={(e) => {
        e?.stopPropagation?.();
        void onPress();
      }}
      activeOpacity={0.85}
      hitSlop={8}
      accessibilityRole="button"
      accessibilityLabel={favorita ? 'Quitar de favoritos' : 'Añadir a favoritos'}
      disabled={busy}
    >
      {busy ? (
        <ActivityIndicator size="small" color="#A20000" />
      ) : (
        <Ionicons
          name={favorita ? 'heart' : 'heart-outline'}
          size={size}
          color={favorita ? '#A20000' : onLight ? '#374151' : '#ffffff'}
        />
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  btn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnOnLight: {
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
});
